import { uploadFile } from './upload-file';

export interface BuildBrandMomentUploadUrlParams {
  adminUrl: string;
  /** The project's account — the owner of the uploaded file. */
  accountId: string;
}

/**
 * Build the REST path the Brand Moment file is uploaded to.
 *
 * `/rest/upload-file/:accountId`, the same endpoint the design-system filepond
 * uses by default. It answers with a PUBLIC `url`, which is what the widget needs
 * to render the Brand Moment for end customers. The previous target,
 * `/rest/bot-manager/.../assets`, was retired with Bot Manager (Agent Manager v2
 * answers it with 404), and a v2 solution file under `assets/` is only served
 * through the authenticated `/rest/agent-manager-file` route, which the widget
 * cannot read.
 */
export const buildBrandMomentUploadUrl = ({
  adminUrl,
  accountId,
}: BuildBrandMomentUploadUrlParams): string =>
  `${adminUrl}/rest/upload-file/${accountId}`;

/** Context fields the Brand Moment upload URL needs. */
export type BrandMomentUploadContextField = 'accountId';

/**
 * Validate the context required to build the Brand Moment upload URL. Returns
 * the missing field (so the caller can surface a matching message) or `null`
 * when everything is present.
 */
export const validateBrandMomentUploadContext = ({
  accountId,
}: {
  accountId?: string;
}): BrandMomentUploadContextField | null => (accountId ? null : 'accountId');

/** Result of the deferred Brand Moment upload. */
export interface BrandMomentUploadResult {
  /** The public url of the uploaded file — what `brandMomentUrl` stores. */
  url: string;
}

/**
 * Perform the real Brand Moment upload now (deferred from file-select to Save,
 * PSTUDIO-3710). Resolves with the public url; rejects with the transport error,
 * or when the endpoint answered without a url, so the Save handler can surface
 * it and abort navigation instead of persisting a transient blob: url.
 */
export const uploadBrandMomentFile = ({
  url,
  file,
}: {
  url: string;
  file: File;
}): Promise<BrandMomentUploadResult> =>
  new Promise((resolve, reject) => {
    uploadFile({
      url,
      file,
      type: 'publicFile',
      fieldName: 'file',
      load: () => undefined,
      onChange: (payload) => {
        const uploadedUrl = (payload as { url?: string } | undefined)?.url;
        if (uploadedUrl) {
          resolve({ url: uploadedUrl });
        } else {
          reject(new Error('Brand Moment upload returned no url'));
        }
      },
      error: (e) => reject(e),
    });
  });
